import { ConfigurationModel } from '@3ddv/utils-igsu';
import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { BehaviorSubject, Observable, ObservableInput, of } from 'rxjs';
import { getMessageError } from '../utils/get-message-error';
import { ModalService } from './modal.service';

type QueryParamsData = {
  OKTA_ID: string;
  VENUE_ID: string;
  email: string;
};

export type PatronData = {
  patronProvenueId: number;
  accountName: string;
  firstName: string;
  lastName: string;
  email: string;
  environment: string;
  token: string;
  venueId: string;
  oktaId: string | null;
};

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private modalService = inject(ModalService);
  private readonly _paramsData: BehaviorSubject<QueryParamsData>;
  public readonly paramsData$: Observable<QueryParamsData>;

  private readonly _patron = new BehaviorSubject<PatronData | null>(null);
  public readonly patron$ = this._patron.asObservable();

  private readonly _availableConfigurations = new BehaviorSubject<ConfigurationModel[] | null>(null);
  public readonly availableConfigurations$ = this._availableConfigurations.asObservable();

  constructor() {
    const sessionParamsData = JSON.parse(sessionStorage.getItem('session_data')!) as QueryParamsData;
    const paramsData = sessionParamsData;

    this._paramsData = new BehaviorSubject(paramsData);
    this.paramsData$ = this._paramsData.asObservable();
  }

  /**
   * Retrieves the current session parameters data.
   */
  public getParamsData(): QueryParamsData {
    return this._paramsData.getValue();
  }

  /**
   * Updates the session parameters data both in the BehaviorSubject and sessionStorage.
   * @param newQueryParams - The new query parameters to set.
   */
  public setParamValues(newQueryParams: Params): void {
    this.setOnStorage(newQueryParams);
    this._paramsData.next(newQueryParams as QueryParamsData);
  }

  /**
   * Persists new query parameters to sessionStorage.
   * @param newQueryParams - The query parameters to persist.
   */
  public setOnStorage(newQueryParams: Params): void {
    sessionStorage.setItem('session_data', JSON.stringify(newQueryParams));
  }

  /**
   * Gets the current patron data.
   * @returns - The current patron data.
   */
  public getPatron = (): PatronData | null => {
    return this._patron.getValue();
  };

  /**
   * Sets the patron data.
   * @param patron - The patron data to set.
   */
  public setPatron(patron: PatronData): void {
    this._patron.next(patron);
  }

  /**
   * Gets the available configurations.
   * @returns - The available configurations.
   */
  public getAvailableConfigurations = (): ConfigurationModel[] | null => {
    return this._availableConfigurations.getValue();
  };

  /**
   * Sets the available configurations.
   * @param configurations - The configurations to set.
   */
  public setAvailableConfigurations = (configurations: ConfigurationModel[]): void => {
    this._availableConfigurations.next(configurations);
  };
  /**
   * Compares two sets of parameters to determine if their keys and values are equal.
   * @param params1 - The first parameter object to compare.
   * @param params2 - The second parameter object to compare.
   * @returns An object containing two boolean properties: keysEqual and valuesEqual.
   */
  public checkParamsEquality(params1: Params, params2: Params): Record<string, boolean> {
    const keys1 = Object.keys(params1 || {});
    const keys2 = Object.keys(params2 || {});

    const keysEqual = keys1.length === keys2.length && keys1.every(key => keys2.includes(key));
    const valuesEqual = keysEqual && keys1.every(key => params1[key] === params2[key]);

    return { keysEqual, valuesEqual };
  }

  /**
   * Handles authentication errors by showing an error message in a modal and providing an option to reload the page.
   *
   * @param {any} err - The error object containing the error details.
   * @param {ModalService} modalService - The modal service used to display the error modal.
   * @returns An observable that emits `undefined` after handling the error.
   */
  public handleAuthenticationError(err: any, modalService: ModalService): ObservableInput<any> {
    const errorMessage = getMessageError(err.error);

    const config = {
      type: 'error',
      text: errorMessage!,
      buttonText: 'Reload page',
      buttonAction: () => location.reload(),
    };
    modalService?.openModal(config);
    return of(undefined);
  }
}
